import React, { useState, useEffect, useCallback } from 'react';
import { cmsService } from '../services/cmsService';
import { toast } from 'react-toastify';

// Hero pages configuration for SVT Consulting
const HERO_PAGES = [
  { id: 'home', name: 'Home', icon: '🏠', description: 'Main landing page hero' },
  { id: 'about', name: 'About', icon: '📖', description: 'Company story page' },
  { id: 'services', name: 'Services', icon: '⚙️', description: 'Services overview page' },
  { id: 'services-av', name: 'Audio/Video', icon: '🎬', description: 'Audio/Video services' },
  { id: 'services-cctv', name: 'CCTV', icon: '📹', description: 'Security camera services' },
  { id: 'services-automation', name: 'Automation', icon: '🏠', description: 'Smart home automation' },
  { id: 'services-software', name: 'Software', icon: '💻', description: 'Software development' },
  { id: 'portfolio', name: 'Portfolio', icon: '📸', description: 'Project portfolio page' },
  { id: 'contact', name: 'Contact', icon: '✉️', description: 'Contact information page' },
];

const WebsiteCMS = () => {
  const [activeTab, setActiveTab] = useState('heroes');
  const [gallery, setGallery] = useState([]);
  const [projects, setProjects] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [labels, setLabels] = useState([]);
  const [heroImages, setHeroImages] = useState({});
  const [services, setServices] = useState([]);
  const [companyInfo, setCompanyInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploadingHero, setUploadingHero] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showTestimonialModal, setShowTestimonialModal] = useState(false);
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [filterLabel, setFilterLabel] = useState('');
  const [savingService, setSavingService] = useState(false);

  const mainTabs = [
    { id: 'heroes', label: 'Hero Images' },
    { id: 'gallery', label: 'Gallery' },
    { id: 'projects', label: 'Projects' },
    { id: 'testimonials', label: 'Testimonials' },
    { id: 'services', label: 'Services' },
    { id: 'settings', label: 'Company Info' },
  ];

  const [uploadForm, setUploadForm] = useState({
    title: '', description: '', media_type: 'image', file: null,
    video_embed_url: '', labels: [], is_featured: false
  });

  const [projectForm, setProjectForm] = useState({
    title: '', client_name: '', short_description: '', full_description: '',
    featured_image: null, before_image: null, after_image: null,
    project_date: '', location: '', services_provided: [], labels: [],
    is_featured: false, is_published: true
  });

  const [testimonialForm, setTestimonialForm] = useState({
    client_name: '', client_title: '', client_company: '', client_photo: null,
    testimonial_text: '', rating: 5, project_id: '', is_featured: false, is_published: true
  });

  const [labelForm, setLabelForm] = useState({ name: '', label_type: 'both', color: '#FF5722' });

  const [serviceForm, setServiceForm] = useState({
    title: '', short_description: '', full_description: '',
    icon_name: '', features: [], image: null, is_published: true, newFeature: ''
  });

  const serviceOptions = ['Audio/Video', 'CCTV', 'Automation', 'Software Development', 'Web Design', 'Mobile Apps'];

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [galleryData, projectsData, testimonialsData, labelsData, servicesData, companyData] = await Promise.all([
        cmsService.getGallery({ label: filterLabel || undefined }),
        cmsService.getProjects(),
        cmsService.getTestimonials(),
        cmsService.getLabels(),
        cmsService.getServices(),
        cmsService.getCompanyInfo()
      ]);
      setGallery(galleryData.items || []);
      setProjects(projectsData || []);
      setTestimonials(testimonialsData || []);
      setLabels(labelsData || []);
      setServices(servicesData || []);
      setCompanyInfo(companyData || {});
      
      try {
        const heroData = await cmsService.getHeroImages();
        const heroMap = {};
        heroData.forEach(hero => { heroMap[hero.page_id] = hero; });
        setHeroImages(heroMap);
      } catch (err) { console.log('Hero images not available yet'); }
    } catch (err) {
      toast.error('Failed to load CMS data');
    } finally {
      setLoading(false);
    }
  }, [filterLabel]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleHeroUpload = async (pageId, file) => {
    if (!file) return;
    setUploadingHero(pageId);
    try {
      const formData = new FormData();
      formData.append('page_id', pageId);
      formData.append('image', file);
      await cmsService.uploadHeroImage(formData);
      toast.success(`Hero image for ${pageId} updated!`);
      fetchData();
    } catch (err) {
      toast.error('Failed to upload hero image');
    } finally {
      setUploadingHero(null);
    }
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('title', uploadForm.title);
      formData.append('description', uploadForm.description);
      formData.append('media_type', uploadForm.media_type);
      formData.append('is_featured', uploadForm.is_featured);
      formData.append('labels', JSON.stringify(uploadForm.labels));
      if (uploadForm.file) formData.append('file', uploadForm.file);
      if (uploadForm.video_embed_url) formData.append('video_embed_url', uploadForm.video_embed_url);

      await cmsService.uploadGalleryItem(formData);
      toast.success('Media uploaded successfully!');
      setShowUploadModal(false);
      resetUploadForm();
      fetchData();
    } catch (err) {
      toast.error('Failed to upload media');
    }
  };

  const handleProjectSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      Object.keys(projectForm).forEach(key => {
        if (['featured_image', 'before_image', 'after_image'].includes(key)) {
          if (projectForm[key]) formData.append(key, projectForm[key]);
        } else if (['services_provided', 'labels'].includes(key)) {
          formData.append(key, JSON.stringify(projectForm[key]));
        } else {
          formData.append(key, projectForm[key]);
        }
      });

      if (selectedItem) {
        await cmsService.updateProject(selectedItem.id, formData);
        toast.success('Project updated!');
      } else {
        await cmsService.createProject(formData);
        toast.success('Project created!');
      }
      setShowProjectModal(false);
      resetProjectForm();
      fetchData();
    } catch (err) {
      toast.error('Failed to save project');
    }
  };

  const handleTestimonialSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      Object.keys(testimonialForm).forEach(key => {
        if (key === 'client_photo' && testimonialForm[key]) {
          formData.append(key, testimonialForm[key]);
        } else if (key !== 'client_photo') {
          formData.append(key, testimonialForm[key]);
        }
      });

      if (selectedItem) {
        await cmsService.updateTestimonial(selectedItem.id, formData);
        toast.success('Testimonial updated!');
      } else {
        await cmsService.createTestimonial(formData);
        toast.success('Testimonial created!');
      }
      setShowTestimonialModal(false);
      resetTestimonialForm();
      fetchData();
    } catch (err) {
      toast.error('Failed to save testimonial');
    }
  };

  const handleLabelSubmit = async (e) => {
    e.preventDefault();
    try {
      await cmsService.createLabel(labelForm);
      toast.success('Label created!');
      setShowLabelModal(false);
      setLabelForm({ name: '', label_type: 'both', color: '#FF5722' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create label');
    }
  };

  const handleServiceSubmit = async (e) => {
    e.preventDefault();
    if (!selectedService) return;
    
    setSavingService(true);
    try {
      const formData = new FormData();
      formData.append('title', serviceForm.title);
      formData.append('short_description', serviceForm.short_description);
      formData.append('full_description', serviceForm.full_description);
      formData.append('icon_name', serviceForm.icon_name);
      formData.append('features', JSON.stringify(serviceForm.features));
      formData.append('is_published', serviceForm.is_published);
      if (serviceForm.image) {
        formData.append('image', serviceForm.image);
      }

      await cmsService.updateService(selectedService.service_key, formData);
      toast.success('Service updated!');
      setShowServiceModal(false);
      setSelectedService(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to update service');
    } finally {
      setSavingService(false);
    }
  };

  const handleDelete = async (id, type) => {
    if (!window.confirm(`Are you sure you want to delete this ${type}?`)) return;
    try {
      if (type === 'gallery') await cmsService.deleteGalleryItem(id);
      else if (type === 'project') await cmsService.deleteProject(id);
      else if (type === 'testimonial') await cmsService.deleteTestimonial(id);
      toast.success(`${type} deleted`);
      fetchData();
    } catch (err) {
      toast.error(`Failed to delete ${type}`);
    }
  };

  const handleToggleFeatured = async (item, type) => {
    try {
      if (type === 'gallery') {
        await cmsService.updateGalleryItem(item.id, { is_featured: !item.is_featured });
      } else if (type === 'project') {
        const formData = new FormData();
        formData.append('is_featured', !item.is_featured);
        await cmsService.updateProject(item.id, formData);
      } else if (type === 'testimonial') {
        const formData = new FormData();
        formData.append('is_featured', !item.is_featured);
        await cmsService.updateTestimonial(item.id, formData);
      }
      toast.success('Updated!');
      fetchData();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleCompanyInfoUpdate = async () => {
    try {
      await cmsService.updateCompanyInfo(companyInfo);
      toast.success('Company info updated!');
    } catch (err) {
      toast.error('Failed to update company info');
    }
  };

  const resetUploadForm = () => setUploadForm({ title: '', description: '', media_type: 'image', file: null, video_embed_url: '', labels: [], is_featured: false });
  const resetProjectForm = () => { setProjectForm({ title: '', client_name: '', short_description: '', full_description: '', featured_image: null, before_image: null, after_image: null, project_date: '', location: '', services_provided: [], labels: [], is_featured: false, is_published: true }); setSelectedItem(null); };
  const resetTestimonialForm = () => { setTestimonialForm({ client_name: '', client_title: '', client_company: '', client_photo: null, testimonial_text: '', rating: 5, project_id: '', is_featured: false, is_published: true }); setSelectedItem(null); };

  const editProject = (project) => {
    setSelectedItem(project);
    setProjectForm({
      title: project.title, client_name: project.client_name || '', short_description: project.short_description || '',
      full_description: project.full_description || '', featured_image: null, before_image: null, after_image: null,
      project_date: project.project_date?.split('T')[0] || '', location: project.location || '',
      services_provided: project.services_provided || [], labels: project.labels?.map(l => l.id) || [],
      is_featured: project.is_featured, is_published: project.is_published
    });
    setShowProjectModal(true);
  };

  const editTestimonial = (testimonial) => {
    setSelectedItem(testimonial);
    setTestimonialForm({
      client_name: testimonial.client_name, client_title: testimonial.client_title || '',
      client_company: testimonial.client_company || '', client_photo: null, testimonial_text: testimonial.testimonial_text,
      rating: testimonial.rating || 5, project_id: testimonial.project_id || '',
      is_featured: testimonial.is_featured, is_published: testimonial.is_published
    });
    setShowTestimonialModal(true);
  };

  const editService = (service) => {
    setSelectedService(service);
    setServiceForm({
      title: service.title || '',
      short_description: service.short_description || '',
      full_description: service.full_description || '',
      icon_name: service.icon_name || '',
      features: service.features || [],
      image: null,
      is_published: service.is_published !== false,
      newFeature: ''
    });
    setShowServiceModal(true);
  };

  const addFeature = () => {
    if (serviceForm.newFeature.trim()) {
      setServiceForm({
        ...serviceForm,
        features: [...serviceForm.features, serviceForm.newFeature.trim()],
        newFeature: ''
      });
    }
  };

  const removeFeature = (index) => {
    setServiceForm({
      ...serviceForm,
      features: serviceForm.features.filter((_, i) => i !== index)
    });
  };

  const formatDate = (date) => new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  const galleryLabels = labels.filter(l => l.label_type === 'gallery' || l.label_type === 'both');

  // Helper to safely create background image URL
  const bgImage = (url) => url ? `url('${url}')` : null;

  const getServiceIcon = (serviceKey) => {
    switch(serviceKey) {
      case 'audio-video': return '🎬';
      case 'cctv': return '📹';
      case 'automation': return '🏠';
      case 'software': return '💻';
      default: return '⚙️';
    }
  };

  const styles = {
    container: { padding: '24px' },
    header: { marginBottom: '24px' },
    title: { fontSize: '28px', fontWeight: '700', margin: '0 0 4px' },
    subtitle: { color: '#6B7280', fontSize: '14px', margin: 0 },
    tabs: { display: 'flex', gap: '32px', borderBottom: '1px solid #E5E7EB', marginBottom: '24px' },
    tab: (active) => ({ padding: '12px 0', fontSize: '15px', fontWeight: active ? '600' : '500', border: 'none', background: 'transparent', cursor: 'pointer', color: active ? '#000' : '#6B7280', borderBottom: active ? '2px solid #FF5722' : '2px solid transparent', marginBottom: '-1px' }),
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' },
    card: { background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
    cardImage: (url) => ({ height: '140px', backgroundImage: bgImage(url), backgroundSize: 'cover', backgroundPosition: 'center', backgroundColor: url ? '#f3f4f6' : '#1a252f', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }),
    cardBody: { padding: '16px' },
    button: { padding: '10px 20px', background: '#FF5722', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
    buttonSecondary: { padding: '10px 20px', background: '#F3F4F6', color: '#374151', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
    modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    modalContent: { background: 'white', borderRadius: '12px', padding: '24px', width: '500px', maxWidth: '90%', maxHeight: '90vh', overflow: 'auto' },
    formGroup: { marginBottom: '16px' },
    label: { display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px', color: '#374151' },
    input: { width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #E5E7EB', fontSize: '14px' },
    textarea: { width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #E5E7EB', fontSize: '14px', resize: 'vertical' },
    badge: { padding: '4px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' },
    empty: { textAlign: 'center', padding: '60px 20px', background: '#F9FAFB', borderRadius: '12px' },
  };

  if (loading) {
    return <div style={styles.container}><div className="spinner"></div></div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Website CMS</h1>
        <p style={styles.subtitle}>Manage content for svtconsulting.co</p>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        {mainTabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={styles.tab(activeTab === tab.id)}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Hero Images Tab */}
      {activeTab === 'heroes' && (
        <div>
          <div style={{ marginBottom: '24px', padding: '16px', background: 'linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '24px' }}>💡</span>
            <div>
              <strong>Pro Tip</strong>
              <span style={{ display: 'block', fontSize: '14px', color: '#E65100' }}>Upload high-quality images (1920x1080 or larger) for best results.</span>
            </div>
          </div>
          
          <div style={styles.grid}>
            {HERO_PAGES.map(page => {
              const heroData = heroImages[page.id];
              const hasImage = heroData?.image_url;
              
              return (
                <div key={page.id} style={{ ...styles.card, border: hasImage ? '2px solid #4CAF50' : '2px solid #E5E7EB' }}>
                  <div style={styles.cardImage(heroData?.image_url)}>
                    {!hasImage && (
                      <div style={{ textAlign: 'center', color: '#9CA3AF' }}>
                        <div style={{ fontSize: '40px' }}>{page.icon}</div>
                        <div style={{ fontSize: '13px' }}>No image set</div>
                      </div>
                    )}
                    {hasImage && (
                      <div style={{ position: 'absolute', top: '12px', right: '12px', background: '#4CAF50', color: 'white', ...styles.badge }}>
                        ✓ Live
                      </div>
                    )}
                  </div>
                  <div style={styles.cardBody}>
                    <h3 style={{ margin: '0 0 4px', fontSize: '17px' }}>{page.name}</h3>
                    <p style={{ margin: '0 0 12px', color: '#6B7280', fontSize: '13px' }}>{page.description}</p>
                    {heroData?.updated_at && (
                      <p style={{ margin: '0 0 12px', color: '#9CA3AF', fontSize: '12px' }}>Updated {formatDate(heroData.updated_at)}</p>
                    )}
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <label style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', ...styles.button, background: uploadingHero === page.id ? '#F3F4F6' : '#FF5722', color: uploadingHero === page.id ? '#666' : 'white', cursor: uploadingHero === page.id ? 'wait' : 'pointer' }}>
                        {uploadingHero === page.id ? '⏳ Uploading...' : (hasImage ? 'Change' : 'Upload')}
                        <input type="file" accept="image/*" onChange={(e) => handleHeroUpload(page.id, e.target.files[0])} disabled={uploadingHero === page.id} style={{ display: 'none' }} />
                      </label>
                      {hasImage && (
                        <a href={heroData.image_url} target="_blank" rel="noopener noreferrer" style={{ ...styles.buttonSecondary, textDecoration: 'none' }}>View</a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Gallery Tab */}
      {activeTab === 'gallery' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
            <div style={{ display: 'flex', gap: '16px' }}>
              <button onClick={() => setFilterLabel('')} style={{ ...styles.buttonSecondary, background: filterLabel === '' ? '#FFF3E0' : '#F3F4F6', border: filterLabel === '' ? '1px solid #FF5722' : 'none' }}>All</button>
              {galleryLabels.map(l => (
                <button key={l.id} onClick={() => setFilterLabel(l.slug)} style={{ ...styles.buttonSecondary, background: filterLabel === l.slug ? '#FFF3E0' : '#F3F4F6', border: filterLabel === l.slug ? '1px solid #FF5722' : 'none' }}>{l.name}</button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => setShowLabelModal(true)} style={styles.buttonSecondary}>+ Label</button>
              <button onClick={() => setShowUploadModal(true)} style={styles.button}>+ Add Media</button>
            </div>
          </div>

          {gallery.length === 0 ? (
            <div style={styles.empty}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📸</div>
              <h3>No Media Yet</h3>
              <p style={{ color: '#6B7280', marginBottom: '20px' }}>Upload photos and videos to display on your website.</p>
              <button onClick={() => setShowUploadModal(true)} style={styles.button}>Upload First Media</button>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px' }}>
              {gallery.map(item => (
                <div key={item.id} style={{ ...styles.card, position: 'relative' }}>
                  {item.is_featured && <div style={{ position: 'absolute', top: '8px', left: '8px', background: '#FF5722', color: 'white', ...styles.badge }}>Featured</div>}
                  <div style={{ height: '140px', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {item.media_type === 'image' ? <img src={item.url} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: '40px' }}>🎬</span>}
                  </div>
                  <div style={{ padding: '12px' }}>
                    <h4 style={{ margin: '0 0 6px', fontSize: '14px' }}>{item.title}</h4>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '10px' }}>
                      {item.labels?.map(label => <span key={label.id} style={{ fontSize: '11px', padding: '2px 8px', background: '#F3F4F6', borderRadius: '4px' }}>{label.name}</span>)}
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button onClick={() => handleToggleFeatured(item, 'gallery')} style={{ flex: 1, padding: '8px', background: item.is_featured ? '#FF5722' : '#f3f4f6', color: item.is_featured ? 'white' : '#374151', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>{item.is_featured ? '★' : '☆'}</button>
                      <button onClick={() => handleDelete(item.id, 'gallery')} style={{ flex: 1, padding: '8px', background: '#FEE2E2', color: '#DC2626', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Projects Tab */}
      {activeTab === 'projects' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
            <button onClick={() => { resetProjectForm(); setShowProjectModal(true); }} style={styles.button}>+ New Project</button>
          </div>

          {projects.length === 0 ? (
            <div style={styles.empty}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏗️</div>
              <h3>No Projects Yet</h3>
              <p style={{ color: '#6B7280', marginBottom: '20px' }}>Add portfolio projects to showcase your work.</p>
              <button onClick={() => setShowProjectModal(true)} style={styles.button}>Add First Project</button>
            </div>
          ) : (
            <div style={styles.grid}>
              {projects.map(project => (
                <div key={project.id} style={{ ...styles.card, opacity: project.is_published ? 1 : 0.6 }}>
                  <div style={{ height: '160px', background: '#f3f4f6', position: 'relative' }}>
                    {project.featured_image_url ? <img src={project.featured_image_url} alt={project.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontSize: '48px' }}>🏗️</div>}
                    {project.is_featured && <div style={{ position: 'absolute', top: '8px', left: '8px', background: '#FF5722', color: 'white', ...styles.badge }}>Featured</div>}
                    {!project.is_published && <div style={{ position: 'absolute', top: '8px', right: '8px', background: '#6B7280', color: 'white', ...styles.badge }}>Draft</div>}
                  </div>
                  <div style={styles.cardBody}>
                    <h3 style={{ margin: '0 0 4px', fontSize: '16px' }}>{project.title}</h3>
                    {project.client_name && <p style={{ margin: '0 0 8px', color: '#6B7280', fontSize: '13px' }}>{project.client_name}</p>}
                    <p style={{ margin: '0 0 12px', color: '#374151', fontSize: '14px' }}>{project.short_description?.substring(0, 100)}{project.short_description?.length > 100 ? '...' : ''}</p>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '12px' }}>
                      {project.services_provided?.map((service, idx) => <span key={idx} style={{ fontSize: '11px', padding: '2px 8px', background: '#FFF3E0', color: '#E65100', borderRadius: '4px' }}>{service}</span>)}
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={() => editProject(project)} style={{ flex: 1, ...styles.buttonSecondary }}>Edit</button>
                      <button onClick={() => handleToggleFeatured(project, 'project')} style={{ padding: '10px', background: project.is_featured ? '#FF5722' : '#F3F4F6', color: project.is_featured ? 'white' : '#374151', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>{project.is_featured ? '★' : '☆'}</button>
                      <button onClick={() => handleDelete(project.id, 'project')} style={{ padding: '10px', background: '#FEE2E2', color: '#DC2626', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>🗑</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Testimonials Tab */}
      {activeTab === 'testimonials' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
            <button onClick={() => { resetTestimonialForm(); setShowTestimonialModal(true); }} style={styles.button}>+ New Testimonial</button>
          </div>

          {testimonials.length === 0 ? (
            <div style={styles.empty}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>💬</div>
              <h3>No Testimonials Yet</h3>
              <p style={{ color: '#6B7280', marginBottom: '20px' }}>Add client testimonials to build trust.</p>
              <button onClick={() => setShowTestimonialModal(true)} style={styles.button}>Add First Testimonial</button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {testimonials.map(t => (
                <div key={t.id} style={{ ...styles.card, padding: '20px', display: 'flex', gap: '16px', alignItems: 'flex-start', opacity: t.is_published ? 1 : 0.6 }}>
                  <div style={{ width: '60px', height: '60px', borderRadius: '50%', backgroundImage: bgImage(t.client_photo_url), backgroundSize: 'cover', backgroundPosition: 'center', backgroundColor: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', flexShrink: 0 }}>
                    {!t.client_photo_url && '👤'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <h3 style={{ margin: 0, fontSize: '16px' }}>{t.client_name}</h3>
                      {t.is_featured && <span style={{ background: '#FF5722', color: 'white', ...styles.badge }}>Featured</span>}
                    </div>
                    <p style={{ margin: '0 0 8px', color: '#6B7280', fontSize: '13px' }}>{t.client_title}{t.client_company && ` at ${t.client_company}`}</p>
                    <div style={{ marginBottom: '8px', color: '#FFA000' }}>{'★'.repeat(t.rating)}{'☆'.repeat(5 - t.rating)}</div>
                    <p style={{ margin: 0, color: '#374151', fontSize: '14px', fontStyle: 'italic' }}>"{t.testimonial_text}"</p>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <button onClick={() => editTestimonial(t)} style={styles.buttonSecondary}>Edit</button>
                    <button onClick={() => handleToggleFeatured(t, 'testimonial')} style={{ ...styles.buttonSecondary, background: t.is_featured ? '#FF5722' : '#F3F4F6', color: t.is_featured ? 'white' : '#374151' }}>{t.is_featured ? '★' : '☆'}</button>
                    <button onClick={() => handleDelete(t.id, 'testimonial')} style={{ ...styles.buttonSecondary, background: '#FEE2E2', color: '#DC2626' }}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Services Tab */}
      {activeTab === 'services' && (
        <div>
          <div style={{ marginBottom: '24px', padding: '16px', background: '#F3F4F6', borderRadius: '12px' }}>
            <p style={{ margin: 0, color: '#6B7280', fontSize: '14px' }}>Edit descriptions and images for each service displayed on your website.</p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {services.map(s => (
              <div key={s.id} style={{ ...styles.card, padding: '20px' }}>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                  <div style={{ width: '80px', height: '80px', borderRadius: '12px', backgroundImage: bgImage(s.image_url), backgroundSize: 'cover', backgroundPosition: 'center', backgroundColor: '#FFF3E0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '32px', flexShrink: 0 }}>
                    {!s.image_url && getServiceIcon(s.service_key)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <h3 style={{ margin: 0, fontSize: '18px' }}>{s.title}</h3>
                      {s.image_url && <span style={{ background: '#4CAF50', color: 'white', ...styles.badge }}>✓ Image</span>}
                    </div>
                    <p style={{ margin: '0 0 12px', color: '#6B7280', fontSize: '14px' }}>{s.short_description}</p>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {s.features?.map((f, i) => <span key={i} style={{ fontSize: '12px', padding: '4px 10px', background: '#F3F4F6', borderRadius: '4px' }}>{f}</span>)}
                    </div>
                  </div>
                  <button onClick={() => editService(s)} style={styles.button}>Edit</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Company Info Tab */}
      {activeTab === 'settings' && (
        <div style={{ ...styles.card, padding: '24px' }}>
          <h3 style={{ margin: '0 0 20px', fontSize: '18px' }}>Company Information</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            {[
              { key: 'company_name', label: 'Company Name' },
              { key: 'tagline', label: 'Tagline' },
              { key: 'phone', label: 'Phone' },
              { key: 'email', label: 'Email' },
            ].map(field => (
              <div key={field.key} style={styles.formGroup}>
                <label style={styles.label}>{field.label}</label>
                <input type="text" value={companyInfo[field.key] || ''} onChange={e => setCompanyInfo({...companyInfo, [field.key]: e.target.value})} style={styles.input} />
              </div>
            ))}
            <div style={{ gridColumn: 'span 2', ...styles.formGroup }}>
              <label style={styles.label}>Address</label>
              <input type="text" value={companyInfo.address || ''} onChange={e => setCompanyInfo({...companyInfo, address: e.target.value})} style={styles.input} />
            </div>
            {[
              { key: 'city', label: 'City' },
              { key: 'state', label: 'State' },
            ].map(field => (
              <div key={field.key} style={styles.formGroup}>
                <label style={styles.label}>{field.label}</label>
                <input type="text" value={companyInfo[field.key] || ''} onChange={e => setCompanyInfo({...companyInfo, [field.key]: e.target.value})} style={styles.input} />
              </div>
            ))}
          </div>
          <h4 style={{ margin: '24px 0 16px', fontSize: '16px' }}>Social Media</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            {['facebook', 'instagram', 'linkedin', 'twitter'].map(social => (
              <div key={social} style={styles.formGroup}>
                <label style={styles.label}>{social.charAt(0).toUpperCase() + social.slice(1)} URL</label>
                <input type="url" value={companyInfo[social] || ''} onChange={e => setCompanyInfo({...companyInfo, [social]: e.target.value})} style={styles.input} />
              </div>
            ))}
          </div>
          <div style={{ marginTop: '24px' }}>
            <button onClick={handleCompanyInfoUpdate} style={styles.button}>Save Changes</button>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h2 style={{ margin: '0 0 20px', fontSize: '20px' }}>Add Media</h2>
            <form onSubmit={handleUploadSubmit}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Type</label>
                <select value={uploadForm.media_type} onChange={e => setUploadForm({...uploadForm, media_type: e.target.value})} style={styles.input}>
                  <option value="image">Image</option>
                  <option value="video">Video</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Title *</label>
                <input type="text" value={uploadForm.title} onChange={e => setUploadForm({...uploadForm, title: e.target.value})} required style={styles.input} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Description</label>
                <textarea value={uploadForm.description} onChange={e => setUploadForm({...uploadForm, description: e.target.value})} rows={3} style={styles.textarea} />
              </div>
              {uploadForm.media_type === 'image' ? (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Upload Image *</label>
                  <input type="file" accept="image/*" onChange={e => setUploadForm({...uploadForm, file: e.target.files[0]})} required />
                </div>
              ) : (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Video URL</label>
                  <input type="url" value={uploadForm.video_embed_url} onChange={e => setUploadForm({...uploadForm, video_embed_url: e.target.value})} placeholder="https://youtube.com/..." style={styles.input} />
                </div>
              )}
              <div style={styles.formGroup}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={uploadForm.is_featured} onChange={e => setUploadForm({...uploadForm, is_featured: e.target.checked})} />
                  Feature on Homepage
                </label>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="button" onClick={() => { setShowUploadModal(false); resetUploadForm(); }} style={{ flex: 1, ...styles.buttonSecondary }}>Cancel</button>
                <button type="submit" style={{ flex: 1, ...styles.button }}>Upload</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Project Modal */}
      {showProjectModal && (
        <div style={styles.modal}>
          <div style={{ ...styles.modalContent, width: '600px' }}>
            <h2 style={{ margin: '0 0 20px', fontSize: '20px' }}>{selectedItem ? 'Edit Project' : 'New Project'}</h2>
            <form onSubmit={handleProjectSubmit}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Project Title *</label>
                <input type="text" value={projectForm.title} onChange={e => setProjectForm({...projectForm, title: e.target.value})} required style={styles.input} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Client Name</label>
                  <input type="text" value={projectForm.client_name} onChange={e => setProjectForm({...projectForm, client_name: e.target.value})} style={styles.input} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Location</label>
                  <input type="text" value={projectForm.location} onChange={e => setProjectForm({...projectForm, location: e.target.value})} style={styles.input} />
                </div>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Short Description</label>
                <textarea value={projectForm.short_description} onChange={e => setProjectForm({...projectForm, short_description: e.target.value})} rows={2} style={styles.textarea} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Full Description</label>
                <textarea value={projectForm.full_description} onChange={e => setProjectForm({...projectForm, full_description: e.target.value})} rows={4} style={styles.textarea} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Services Provided</label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {serviceOptions.map(s => (
                    <label key={s} style={{ padding: '6px 12px', background: projectForm.services_provided.includes(s) ? '#FFF3E0' : '#F3F4F6', borderRadius: '6px', cursor: 'pointer', border: projectForm.services_provided.includes(s) ? '1px solid #FF5722' : '1px solid transparent' }}>
                      <input type="checkbox" checked={projectForm.services_provided.includes(s)} onChange={e => setProjectForm({...projectForm, services_provided: e.target.checked ? [...projectForm.services_provided, s] : projectForm.services_provided.filter(x => x !== s)})} style={{ display: 'none' }} />
                      {s}
                    </label>
                  ))}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', ...styles.formGroup }}>
                <div><label style={styles.label}>Featured Image</label><input type="file" accept="image/*" onChange={e => setProjectForm({...projectForm, featured_image: e.target.files[0]})} /></div>
                <div><label style={styles.label}>Before Image</label><input type="file" accept="image/*" onChange={e => setProjectForm({...projectForm, before_image: e.target.files[0]})} /></div>
                <div><label style={styles.label}>After Image</label><input type="file" accept="image/*" onChange={e => setProjectForm({...projectForm, after_image: e.target.files[0]})} /></div>
              </div>
              <div style={{ display: 'flex', gap: '16px', ...styles.formGroup }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={projectForm.is_featured} onChange={e => setProjectForm({...projectForm, is_featured: e.target.checked})} />
                  Feature on Homepage
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!projectForm.is_published} onChange={e => setProjectForm({...projectForm, is_published: !e.target.checked})} />
                  Save as Draft
                </label>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="button" onClick={() => { setShowProjectModal(false); resetProjectForm(); }} style={{ flex: 1, ...styles.buttonSecondary }}>Cancel</button>
                <button type="submit" style={{ flex: 1, ...styles.button }}>{selectedItem ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Testimonial Modal */}
      {showTestimonialModal && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h2 style={{ margin: '0 0 20px', fontSize: '20px' }}>{selectedItem ? 'Edit Testimonial' : 'New Testimonial'}</h2>
            <form onSubmit={handleTestimonialSubmit}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Client Name *</label>
                <input type="text" value={testimonialForm.client_name} onChange={e => setTestimonialForm({...testimonialForm, client_name: e.target.value})} required style={styles.input} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Title/Position</label>
                  <input type="text" value={testimonialForm.client_title} onChange={e => setTestimonialForm({...testimonialForm, client_title: e.target.value})} style={styles.input} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Company</label>
                  <input type="text" value={testimonialForm.client_company} onChange={e => setTestimonialForm({...testimonialForm, client_company: e.target.value})} style={styles.input} />
                </div>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Testimonial *</label>
                <textarea value={testimonialForm.testimonial_text} onChange={e => setTestimonialForm({...testimonialForm, testimonial_text: e.target.value})} required rows={4} style={styles.textarea} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Rating</label>
                  <select value={testimonialForm.rating} onChange={e => setTestimonialForm({...testimonialForm, rating: parseInt(e.target.value)})} style={styles.input}>
                    {[5, 4, 3, 2, 1].map(r => <option key={r} value={r}>{r} Star{r > 1 ? 's' : ''}</option>)}
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Client Photo</label>
                  <input type="file" accept="image/*" onChange={e => setTestimonialForm({...testimonialForm, client_photo: e.target.files[0]})} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '16px', ...styles.formGroup }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={testimonialForm.is_featured} onChange={e => setTestimonialForm({...testimonialForm, is_featured: e.target.checked})} />
                  Featured
                </label>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="button" onClick={() => { setShowTestimonialModal(false); resetTestimonialForm(); }} style={{ flex: 1, ...styles.buttonSecondary }}>Cancel</button>
                <button type="submit" style={{ flex: 1, ...styles.button }}>{selectedItem ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Label Modal */}
      {showLabelModal && (
        <div style={styles.modal}>
          <div style={{ ...styles.modalContent, width: '400px' }}>
            <h2 style={{ margin: '0 0 20px', fontSize: '20px' }}>New Label</h2>
            <form onSubmit={handleLabelSubmit}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Label Name *</label>
                <input type="text" value={labelForm.name} onChange={e => setLabelForm({...labelForm, name: e.target.value})} required style={styles.input} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Type</label>
                <select value={labelForm.label_type} onChange={e => setLabelForm({...labelForm, label_type: e.target.value})} style={styles.input}>
                  <option value="both">Both Gallery & Projects</option>
                  <option value="gallery">Gallery Only</option>
                  <option value="project">Projects Only</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Color</label>
                <input type="color" value={labelForm.color} onChange={e => setLabelForm({...labelForm, color: e.target.value})} style={{ width: '100%', height: '40px', borderRadius: '6px', border: '1px solid #E5E7EB' }} />
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="button" onClick={() => setShowLabelModal(false)} style={{ flex: 1, ...styles.buttonSecondary }}>Cancel</button>
                <button type="submit" style={{ flex: 1, ...styles.button }}>Create</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Service Edit Modal */}
      {showServiceModal && selectedService && (
        <div style={styles.modal}>
          <div style={{ ...styles.modalContent, width: '600px' }}>
            <h2 style={{ margin: '0 0 20px', fontSize: '20px' }}>Edit Service: {selectedService.title}</h2>
            <form onSubmit={handleServiceSubmit}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Service Title</label>
                <input 
                  type="text" 
                  value={serviceForm.title} 
                  onChange={e => setServiceForm({...serviceForm, title: e.target.value})} 
                  style={styles.input} 
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Short Description</label>
                <textarea 
                  value={serviceForm.short_description} 
                  onChange={e => setServiceForm({...serviceForm, short_description: e.target.value})} 
                  rows={2} 
                  style={styles.textarea}
                  placeholder="Brief description shown on homepage cards"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Full Description</label>
                <textarea 
                  value={serviceForm.full_description} 
                  onChange={e => setServiceForm({...serviceForm, full_description: e.target.value})} 
                  rows={4} 
                  style={styles.textarea}
                  placeholder="Detailed description for the service page"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Service Image</label>
                {selectedService.image_url && (
                  <div style={{ marginBottom: '8px' }}>
                    <img 
                      src={selectedService.image_url} 
                      alt={selectedService.title} 
                      style={{ width: '120px', height: '80px', objectFit: 'cover', borderRadius: '8px' }}
                    />
                    <span style={{ marginLeft: '12px', color: '#6B7280', fontSize: '13px' }}>Current image</span>
                  </div>
                )}
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={e => setServiceForm({...serviceForm, image: e.target.files[0]})} 
                />
                <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#9CA3AF' }}>
                  Recommended: 800x600px or larger
                </p>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Features</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                  {serviceForm.features.map((feature, index) => (
                    <span 
                      key={index} 
                      style={{ 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        gap: '6px',
                        padding: '6px 12px', 
                        background: '#FFF3E0', 
                        borderRadius: '6px',
                        fontSize: '13px'
                      }}
                    >
                      {feature}
                      <button 
                        type="button"
                        onClick={() => removeFeature(index)}
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          cursor: 'pointer', 
                          color: '#DC2626',
                          fontSize: '16px',
                          padding: 0,
                          lineHeight: 1
                        }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input 
                    type="text" 
                    value={serviceForm.newFeature}
                    onChange={e => setServiceForm({...serviceForm, newFeature: e.target.value})}
                    onKeyPress={e => { if (e.key === 'Enter') { e.preventDefault(); addFeature(); }}}
                    placeholder="Add a feature..."
                    style={{ ...styles.input, flex: 1 }}
                  />
                  <button 
                    type="button" 
                    onClick={addFeature}
                    style={styles.buttonSecondary}
                  >
                    Add
                  </button>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '24px' }}>
                <button 
                  type="button" 
                  onClick={() => { setShowServiceModal(false); setSelectedService(null); }} 
                  style={{ flex: 1, ...styles.buttonSecondary }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={savingService}
                  style={{ 
                    flex: 1, 
                    ...styles.button,
                    opacity: savingService ? 0.7 : 1,
                    cursor: savingService ? 'wait' : 'pointer'
                  }}
                >
                  {savingService ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebsiteCMS;
